import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { AlertTriangle, ExternalLink, X } from 'lucide-react';
import { Column } from 'components/display/Table/types';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { GenericLoading } from 'components/ui/Loader';
import { ReceiptTypeBadge } from 'components/domain';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import {
  FacetQueryCommand,
  INCLUDE_TESTS_BOOLEAN_FACET,
  mapFacetFiltersToFiltersDto
} from 'shared/contexts/table-context/facetFilters';
import { buildExportCommand, exportListCsv } from 'shared/contexts/table-context/exportCsv';
import { paymentScheduleAPI } from 'services/swagger-api';
import { InstallmentDetailDto, QuittanceListItem } from 'api/payment-schedule';
import { TableResponse } from 'slices/generic-table/genericTableSlice';
import { formatDay } from 'core/utils/dates';

const eur = (n?: number | null): string =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n ?? 0);

// dueDate / withdrawalDate = dates CALENDAIRES : découpe de string, zéro fuseau
const fmtDate = (iso?: string | Date | null): string => formatDay(iso);

/** Couleur du badge selon le statut d'échéance (8 statuts). */
const STATUS_CLASS: Record<string, string> = {
  PLANIFIE: 'bg-slate-100 text-slate-600', // À venir
  TRANSMIS: 'bg-blue-100 text-blue-800', // En cours
  PAYE: 'bg-green-100 text-green-800', // Payé
  IMPAYE: 'bg-red-100 text-red-800', // Impayé
  REJETE: 'bg-red-100 text-red-800', // Rejeté
  RETARD: 'bg-orange-100 text-orange-800', // En retard
  PROBLEME_TRANSMISSION: 'bg-amber-200 text-amber-900', // Problème de transmission
  ANNULE: 'bg-gray-100 text-gray-400 line-through' // Annulé
};
const statusBadge = (code: string, label: string) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      STATUS_CLASS[code] ?? 'bg-gray-100 text-gray-600'
    }`}
  >
    {label}
  </span>
);

/** Badge d'état agrégé de la ligne (stateCode, grossier). */
const STATE_CLASS: Record<string, string> = {
  ERREUR: 'bg-red-100 text-red-800',
  EN_RETARD: 'bg-orange-100 text-orange-800',
  A_VENIR: 'bg-blue-100 text-blue-800',
  SOLDEE: 'bg-green-100 text-green-800'
};
const STATE_LABEL: Record<string, string> = {
  ERREUR: 'En erreur',
  EN_RETARD: 'En retard',
  A_VENIR: 'À venir',
  SOLDEE: 'Soldée'
};

/**
 * Style du badge de mode : les modes INTERNES (prélèvement chez nous) sont mis en avant
 * (couleur pleine + gras) ; l'externe (partenaire) est volontairement discret.
 */
const MODE_STYLE: Record<string, string> = {
  EMANDAT: 'bg-primary/10 text-primary font-semibold ring-1 ring-inset ring-primary/30',
  CSV_HBS: 'bg-teal-100 text-teal-800 font-semibold ring-1 ring-inset ring-teal-300',
  EXTERNE: 'bg-gray-50 text-gray-400 ring-1 ring-inset ring-gray-200'
};
const INTERNAL_MODES = new Set(['EMANDAT', 'CSV_HBS']);

/**
 * Rendu « donnée de test » : on floute la donnée pour qu'elle soit clairement non
 * exploitable (on garde le montant d'échéance lisible et les actions actives, gérées
 * à part). Appliqué au contenu des cellules non essentielles quand row.isTest.
 */
const TEST_BLUR = 'blur-[2.5px] select-none opacity-50';
const blurIfTest = (isTest: boolean, node: React.ReactNode): React.ReactNode =>
  isTest ? (
    <span className={TEST_BLUR} aria-hidden title="Donnée de test">
      {node}
    </span>
  ) : (
    node
  );

/** Détail de l'échéancier — chargé à la demande à l'ouverture du panneau. */
const InstallmentsDetail: React.FC<{ quittanceId: string }> = ({ quittanceId }) => {
  const [rows, setRows] = useState<InstallmentDetailDto[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    paymentScheduleAPI
      .getQuittanceInstallments({ id: quittanceId })
      .then((res) => !cancelled && setRows(res))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [quittanceId]);

  if (error) return <p className="text-sm text-red-600">Impossible de charger l'échéancier.</p>;
  if (!rows) return <div className="py-6"><GenericLoading loading isParentSized /></div>;
  if (rows.length === 0) return <p className="text-sm text-gray-500">Aucune échéance.</p>;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* La valeur est la même sur toutes les échéances de la quittance : on la lit sur
          la première et on l'affiche une fois, plutôt qu'en colonne redondante. */}
      {rows[0]?.receiptType && (
        <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
          <span className="text-xs font-medium text-gray-500">Type de quittance</span>
          <ReceiptTypeBadge receiptType={rows[0].receiptType} compact />
        </div>
      )}
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-100 text-xs uppercase tracking-wider text-gray-500">
          <tr>
            {['#', 'Échéance', 'Prélevée le', 'Montant', 'Statut', 'Tent.', 'Rejet', 'RUM'].map(
              (h) => (
                <th key={h} className="px-3 py-2 text-left font-semibold">
                  {h}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((it) => (
            <tr key={it.id} className="hover:bg-gray-50">
              <td className="px-3 py-2 text-gray-500">{it.number}</td>
              <td className="px-3 py-2">{fmtDate(it.dueDate)}</td>
              <td className="px-3 py-2 text-gray-600">{fmtDate(it.withdrawalDate)}</td>
              <td className="px-3 py-2 font-medium">{eur(it.amount)}</td>
              <td className="px-3 py-2">{statusBadge(it.statusCode, it.statusLabel)}</td>
              <td className="px-3 py-2 text-center text-gray-600">{it.attempts}</td>
              <td className="px-3 py-2 text-xs text-gray-600">
                {it.rejectLabel ? `${it.rejectLabel}${it.rejectCode ? ` (${it.rejectCode})` : ''}` : '—'}
              </td>
              <td className="px-3 py-2 font-mono text-xs text-gray-500">{it.rum || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

/** Panneau latéral droit : résumé de la quittance + échéancier complet. */
const QuittanceDetailDrawer: React.FC<{
  quittance: QuittanceListItem | null;
  onClose: () => void;
}> = ({ quittance, onClose }) => {
  const q = quittance;
  const pct = q && q.installmentsCount > 0 ? (q.paidCount / q.installmentsCount) * 100 : 0;
  const stat = (label: string, value: React.ReactNode) => (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{value}</dd>
    </div>
  );

  return (
    <Dialog open={!!q} onClose={onClose} className="relative z-[60]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition duration-200 data-[closed]:opacity-0"
      />
      <div className="fixed inset-0 flex justify-end">
        <DialogPanel
          transition
          className="flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl transition duration-300 ease-out data-[closed]:translate-x-full"
        >
          {q && (
            <>
              {/* En-tête */}
              <div className="flex items-start justify-between bg-primary px-6 py-4">
                <div>
                  <p className="text-xs font-medium text-white/70">Quittance {q.receiptNumber}</p>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-white">{q.subscriberName}</h3>
                    {q.isTest && (
                      <span className="inline-flex items-center rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-950">
                        Test
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {/* Résumé */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-xl border border-gray-100 bg-gray-50/60 p-4 sm:grid-cols-3">
                  {stat(
                    'Contrat',
                    <div>
                      <div className="font-mono text-xs text-gray-700">{q.contractNumber}</div>
                      <div className="text-xs text-gray-500">{q.productName}</div>
                    </div>
                  )}
                  {stat(
                    'Mode',
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                      {q.withdrawalModeLabel}
                    </span>
                  )}
                  {stat(
                    'État',
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        STATE_CLASS[q.stateCode] ?? 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {STATE_LABEL[q.stateCode] ?? q.stateCode}
                    </span>
                  )}
                  {stat('Périodicité', q.periodicityLabel)}
                  {stat('Total', <span className="font-semibold">{eur(q.totalAmount)}</span>)}
                  {stat('Reste', <span className="font-semibold text-gray-900">{eur(q.remainingAmount)}</span>)}
                  {stat(
                    'Erreurs',
                    q.errorCount > 0 ? (
                      <span className="font-bold text-red-700">{q.errorCount}</span>
                    ) : (
                      <span className="text-gray-400">Aucune</span>
                    )
                  )}
                  <div className="col-span-2 sm:col-span-3">
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                      Progression
                    </dt>
                    <div className="mt-1 flex items-center justify-between text-xs text-gray-600">
                      <span>{q.paidCount}/{q.installmentsCount} échéances payées</span>
                      <span>{eur(q.paidAmount)} / {eur(q.totalAmount)}</span>
                    </div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200">
                      <div className="h-1.5 rounded-full bg-primary" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>

                {/* Échéancier */}
                <h4 className="mb-2 mt-6 text-sm font-semibold text-gray-900">Échéancier</h4>
                <InstallmentsDetail quittanceId={q.id} />
              </div>
            </>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default function PrelevementsAdminList() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<QuittanceListItem | null>(null);

  const columns: Column<QuittanceListItem>[] = useMemo(
    () => [
      {
        key: 'subscriberName',
        label: 'Souscripteur',
        sortKey: 'subscriber',
        render: (v, row) => (
          <div className="flex items-center gap-2">
            {blurIfTest(
              row.isTest,
              <span className="font-semibold text-gray-900">{v as string}</span>
            )}
            {row.isTest && (
              <span className="inline-flex items-center rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-950">
                Test
              </span>
            )}
          </div>
        )
      },
      {
        key: 'contractNumber',
        label: 'Contrat',
        sortKey: 'identifier',
        render: (v, row) =>
          blurIfTest(
            row.isTest,
            <div className="leading-tight">
              <div className="font-mono text-xs text-gray-700">{v as string}</div>
              <div className="text-xs text-gray-500">{row.productName}</div>
            </div>
          )
      },
      {
        key: 'contractEffectiveDate',
        label: "Date d'effet du contrat",
        sortKey: 'effectiveDate',
        render: (v, row) => blurIfTest(row.isTest, <span className="text-sm text-gray-700">{fmtDate(v as string)}</span>)
      },
      {
        key: 'withdrawalModeLabel',
        label: 'Mode',
        render: (v, row) => {
          const internal = INTERNAL_MODES.has(row.withdrawalModeCode);
          return blurIfTest(
            row.isTest,
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs ${
                MODE_STYLE[row.withdrawalModeCode] ?? 'bg-gray-100 text-gray-600'
              }`}
            >
              {internal && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
              {(v as string) || '—'}
            </span>
          );
        }
      },
      {
        key: 'highlightedInstallment',
        label: 'Échéance à surveiller',
        sortKey: 'nextDueDate',
        render: (_v, row) => {
          const h = row.highlightedInstallment;
          if (!h) return <span className="text-gray-400">—</span>;
          return (
            <div className="leading-tight">
              {/* Montant conservé net même en test ; le reste est flouté. */}
              <div className="text-base font-bold text-gray-900">{eur(h.amount)}</div>
              {blurIfTest(
                row.isTest,
                <>
                  <div className="text-xs text-gray-500">échéance du {fmtDate(h.dueDate)}</div>
                  <div className="mt-1">{statusBadge(h.statusCode, h.statusLabel)}</div>
                </>
              )}
            </div>
          );
        }
      },
      {
        key: 'paidCount',
        label: 'Progression',
        render: (_v, row) => {
          // Prélèvement externe : on n'a pas la main -> pas de progression pertinente
          if (row.withdrawalModeCode === 'EXTERNE') {
            return <span className="text-xs italic text-gray-400">Géré en externe</span>;
          }
          const pct = row.installmentsCount > 0 ? (row.paidCount / row.installmentsCount) * 100 : 0;
          return blurIfTest(
            row.isTest,
            <div className="min-w-[120px]">
              <div className="flex justify-between text-xs text-gray-600">
                <span>{row.paidCount}/{row.installmentsCount}</span>
                <span>{eur(row.paidAmount)} / {eur(row.totalAmount)}</span>
              </div>
              <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200">
                <div className="h-1.5 rounded-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        }
      },
      {
        key: 'remainingAmount',
        label: 'Reste',
        sortKey: 'remainingAmount',
        render: (v, row) =>
          row.withdrawalModeCode === 'EXTERNE' ? (
            <span className="text-gray-300">—</span>
          ) : (
            blurIfTest(
              row.isTest,
              <span className="text-sm font-medium text-gray-900">{eur(v as number)}</span>
            )
          )
      },
      {
        key: 'errorCount',
        label: 'Erreurs',
        sortKey: 'errorCount',
        render: (v, row) =>
          (v as number) > 0 ? (
            blurIfTest(
              row.isTest,
              <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-800">
                <AlertTriangle className="h-3.5 w-3.5" />
                {v as number}
              </span>
            )
          ) : (
            <span className="text-gray-300">—</span>
          )
      },
      {
        key: 'stateCode',
        label: 'État',
        render: (v, row) =>
          blurIfTest(
            row.isTest,
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                STATE_CLASS[v as string] ?? 'bg-gray-100 text-gray-600'
              }`}
            >
              {STATE_LABEL[row.stateCode] ?? row.stateCode}
            </span>
          )
      }
    ],
    []
  );

  const fetchQuittances = useCallback(
    async (params: any): Promise<TableResponse<QuittanceListItem>> => {
      const result = await paymentScheduleAPI.getAdminQuittances({
        getGenericListCommandDto: {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          searchString: params.searchString || undefined,
          // sortBy null par défaut => tri intelligent du back (erreurs d'abord…)
          sortBy: params.sortBy ?? null,
          sortDirection: params.sortDirection,
          filters: mapFacetFiltersToFiltersDto(params.filters)
        }
      });
      const items = result.items || [];
      return {
        items,
        totalItems: result.totalCount || items.length,
        totalPages: Math.ceil((result.totalCount || items.length) / params.pageSize),
        currentPage: params.pageNumber
      };
    },
    []
  );

  const fetchFacets = useCallback(
    (command: FacetQueryCommand) =>
      paymentScheduleAPI.getAdminQuittanceListFacets({ getGenericListCommandDto: command }),
    []
  );

  return (
    <>
      <ServerTable<QuittanceListItem>
        apiConfig={{ fetchData: fetchQuittances }}
        columns={columns}
        serverSorting
        centerColumns
        columnCustomization={{ storageKey: 'bo-prelevements' }}
        // Lignes de test mises en évidence (fond ambré + badge TEST) quand elles
        // remontent, c.-à-d. quand « Inclure tests » est coché. Le champ isTest est
        // toujours renvoyé sur la ligne.
        getRowClassName={(row) => (row.isTest ? 'bg-amber-50 hover:bg-amber-100/70' : '')}
        facets={{ fetchFacets, booleanFacets: INCLUDE_TESTS_BOOLEAN_FACET }}
        onExport={(p) =>
          exportListCsv(
            '/api/backoffice/echeances-paiement/admin/liste/csv',
            buildExportCommand(p, mapFacetFiltersToFiltersDto(p.filters))
          )
        }
        // Détail de l'échéancier dans un panneau latéral droit (bouton œil + clic ligne)
        onRowClick={(row) => setSelected(row)}
        actions={{
          row: [
            {
              // Aperçu rapide dans le panneau latéral
              label: 'apercu',
              render: (row: QuittanceListItem, index: number) => (
                <Button key={index} color="slate" round onClick={() => setSelected(row)}>
                  <EyeIcon />
                </Button>
              )
            },
            {
              // Redirection vers la page de détail complète de l'échéancier.
              // paymentScheduleId (distinct du quittanceId) fourni par le back ;
              // bouton masqué tant qu'il est absent, pour éviter un lien cassé.
              label: 'detail-page',
              render: (row: QuittanceListItem, index: number) =>
                row.paymentScheduleId ? (
                  <Button
                    key={index}
                    color="slate"
                    round
                    onClick={() =>
                      navigate(`/back-office/prelevements/${row.paymentScheduleId}?tab=info`)
                    }
                  >
                    <ExternalLink size={16} />
                  </Button>
                ) : (
                  <React.Fragment key={index} />
                )
            }
          ]
        }}
      />
      <QuittanceDetailDrawer quittance={selected} onClose={() => setSelected(null)} />
    </>
  );
}
